import React, { useState, useRef } from "react";
import styled from "styled-components";
import ChatInputMark from '../img/chat_input_mark.svg';
import useStore from "../store.js";
import DisclosureChevron from "./DisclosureChevron.jsx";
import { formatChatQuestionSummary, resolveChatDisplayQuestion } from "../utils/chatQuestion.js";

const USER_ISSUE_MAX_LENGTH = 10;

const ChatSearchHistory = ({
  isMobileMode = false,
  isCollapsible = false,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const [isInputActive, setIsInputActive] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  const {
    chatHistory, updatedQuestionHistory, updatedQuestion,
    questionHistoryViewIndex,
    isAIResponding, isChatDone,
    issues, userIssues, aiIssues, addUserIssue, removeIssue,
  } = useStore();

  const contentText = formatChatQuestionSummary(resolveChatDisplayQuestion({
    updatedQuestion,
    updatedQuestionHistory,
    questionHistoryViewIndex,
    chatHistory,
  }));

  // 쟁점 직접입력 핸들러
  const handleAddIssue = () => {
    const trimmed = inputValue.trim();
    if (trimmed) addUserIssue(inputValue);
    setInputValue('');
    setIsInputActive(false);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') handleAddIssue();
    if (e.key === 'Escape') { setInputValue(''); setIsInputActive(false); }
  };

  const canAddIssue = issues.length < 3;
  const historyLength = updatedQuestionHistory.length;
  const safeHistoryIndex = historyLength === 0
    ? 0
    : Math.min(Math.max(0, questionHistoryViewIndex), historyLength - 1);
  const mobileTitle = isMobileMode && isChatDone && historyLength > 0
    ? `업데이트된 질문으로 판례를 검색할까요? (${safeHistoryIndex + 1}/${historyLength})`
    : '업데이트된 질문으로 판례를 검색할까요?';
  const isContentCollapsed = isCollapsible && isCollapsed;
  const isDisclosureButton = isMobileMode && isCollapsible;

  if (!contentText) return null;

  return (
    <Wrapper
      $isMobileMode={isMobileMode}
      $isCollapsed={isContentCollapsed}
      data-testid="chat-search-history"
    >
      <HeaderRow
        as={isDisclosureButton ? 'button' : 'div'}
        {...(isDisclosureButton
          ? {
            type: 'button',
            onClick: onToggleCollapse,
            'aria-expanded': !isContentCollapsed,
            'aria-label': isContentCollapsed ? '업데이트 질문 펼치기' : '업데이트 질문 접기',
          }
          : {})}
        $isDisclosureButton={isDisclosureButton}
        $isCollapsed={isContentCollapsed}
      >
        <HeaderMain>
          <Logo src={ChatInputMark} alt="로고" />
          <HeaderText $isMobileMode={isMobileMode}>{mobileTitle}</HeaderText>
        </HeaderMain>
        {isDisclosureButton && (
          <DisclosureChevron
            // 하단 고정 카드이므로 접힌 상태에서는 위로 펼치고,
            // 펼친 상태에서는 아래로 접는 다음 동작의 방향을 가리킨다.
            expanded={isContentCollapsed}
            data-testid="update-question-chevron"
          />
        )}
      </HeaderRow>

      {!isContentCollapsed && (
        <>
          <ContentText $isMobileMode={isMobileMode}>
            {contentText}
          </ContentText>
          {/* 쟁점 섹션 */}
          <IssueDivider data-testid="issue-divider" />
          <IssuesTitleRow
            $isMobileMode={isMobileMode}
            data-testid="issues-title-row"
          >
            <IssuesTitle $isMobileMode={isMobileMode}>법률 쟁점 검색 포인트</IssuesTitle>
            <IssuesSubtitle $isMobileMode={isMobileMode}>
              {isMobileMode ? '최대 3개까지 설정 가능' : '최대 3개까지 설정할 수 있습니다'}
            </IssuesSubtitle>
          </IssuesTitleRow>
          <IssuesChipRow $isMobileMode={isMobileMode}>
            {issues.length === 0 && (
              <EmptyIssuesText>
                쟁점이 없어도 현재 질문으로 검색할 수 있습니다.
              </EmptyIssuesText>
            )}
            {aiIssues.map(issue => (
              <AIChip key={`ai-${issue}`}>
                <AIBadge>AI 제안</AIBadge>
                <ChipText $isMobileMode={isMobileMode}>{issue}</ChipText>
                <ChipCloseButton onClick={() => removeIssue(issue)} aria-label="삭제">
                  <CloseX />
                </ChipCloseButton>
              </AIChip>
            ))}
            {userIssues.map(issue => (
              <UserChip key={`user-${issue}`}>
                <UserBadge>직접 입력</UserBadge>
                <ChipText $isMobileMode={isMobileMode}>{issue}</ChipText>
                <ChipCloseButton onClick={() => removeIssue(issue)} aria-label="삭제">
                  <CloseX />
                </ChipCloseButton>
              </UserChip>
            ))}
            {canAddIssue && (
              isInputActive ? (
                <InputChipWrapper $isMobileMode={isMobileMode}>
                  <IssueInput
                    ref={inputRef}
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    autoFocus
                    placeholder="쟁점을 입력하세요"
                    maxLength={USER_ISSUE_MAX_LENGTH}
                    $isMobileMode={isMobileMode}
                  />
                  {inputValue.trim() ? (
                    <ConfirmBtn onClick={handleAddIssue} aria-label="확인">
                      <TypedCheckIcon />
                    </ConfirmBtn>
                  ) : (
                    <CancelBtn onClick={() => { setInputValue(''); setIsInputActive(false); }} aria-label="취소">
                      <IdleCheckIcon />
                    </CancelBtn>
                  )}
                </InputChipWrapper>
              ) : (
                <AddIssueButton
                  onClick={() => setIsInputActive(true)}
                  disabled={isAIResponding}
                  $isMobileMode={isMobileMode}
                >
                  <span>직접입력</span>
                  <PlusIcon />
                </AddIssueButton>
              )
            )}
          </IssuesChipRow>
        </>
      )}
    </Wrapper>
  );
};

// 아이콘 컴포넌트
const CloseX = () => (
  <svg width="7" height="7" viewBox="0 0 7 7" fill="none">
    <path d="M1 1L6 6M6 1L1 6" stroke="white" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

const TypedCheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="12" fill="#292929" />
    <path d="M8 12L10.8571 15L16 9" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IdleCheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="12" fill="#F5F5F5" />
    <path d="M8 12L10.8571 15L16 9" stroke="#CCCCCC" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
    <path d="M4.5 1V8M1 4.5H8" stroke="rgba(0,0,0,0.5)" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

export default ChatSearchHistory;

const Wrapper = styled.div`
  width: 680px;
  box-sizing: border-box;
  border-radius: 24px;
  background: #F5F5F5;
  padding: 20px 24px 20px 24px;
  display: flex;
  flex-direction: column;
  ${({ $isMobileMode, $isCollapsed }) =>
    $isMobileMode && `
      width: calc(100% - 48px);
      max-width: 536px;
      border-radius: 16px;
      padding: ${$isCollapsed ? '14px 18px' : '16px 18px'};
      flex-grow: 0;
      flex-shrink: 0;
      align-self: center;
      justify-content: flex-start;
      height: auto;
      min-height: 0;
      overflow: hidden;
      box-sizing: border-box;
      @media (min-width: 600px) {
        border-radius: 24px;
        padding: ${$isCollapsed ? '16px 24px' : '20px 24px'};
      }
    `}

  @media (max-width: 1140px) and (min-width: 600px) {
    width: 536px;
  }

  @media (max-width: 599px) {
    width: calc(100% - 48px);
    max-width: 536px;
    border-radius: 16px;
    padding: ${({ $isCollapsed }) => ($isCollapsed ? '14px 18px' : '16px 18px')};
    flex-grow: 0;
    flex-shrink: 0;
    align-self: center;
    justify-content: flex-start;
    height: auto;
    min-height: 0;
    overflow: hidden;
    box-sizing: border-box;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: 0 0 ${({ $isCollapsed }) => ($isCollapsed ? '0' : '10px')};
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: ${({ $isDisclosureButton }) => ($isDisclosureButton ? 'pointer' : 'default')};

  &:focus-visible {
    outline: 2px solid #00c968;
    outline-offset: 4px;
    border-radius: 8px;
  }
`;

const HeaderMain = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const Logo = styled.img`
  width: 14px;
  height: 14px;
  flex-shrink: 0;

  @media (max-width: 599px) {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
`;

const HeaderText = styled.div`
  color: rgba(0, 0, 0, 0.8);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.28px;

  @media (max-width: 599px) {
    font-size: ${({ $isMobileMode }) => ($isMobileMode ? '13px' : '14px')};
    letter-spacing: ${({ $isMobileMode }) => ($isMobileMode ? '-0.26px' : '-0.28px')};
  }
`;

const ContentText = styled.div`
  color: rgba(0, 0, 0, 0.70);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.28px;
  flex-shrink: 0;
  ${({ $isMobileMode }) =>
    $isMobileMode && `
      font-size: 13px;
      line-height: 22px;
      letter-spacing: -0.26px;
      max-width: 100%;
      flex-grow: 0;
      @media (min-width: 600px) {
        max-width: 100%;
        font-size: 14px;
        line-height: 24px;
        letter-spacing: -0.28px;
      }
      white-space: pre-wrap;
      word-break: break-word;
      display: -webkit-box;
      -webkit-line-clamp: 6;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}

  @media (max-width: 599px) {
    font-size: ${({ $isMobileMode }) => ($isMobileMode ? '13px' : '13px')};
    line-height: ${({ $isMobileMode }) => ($isMobileMode ? '22px' : '22px')};
    letter-spacing: ${({ $isMobileMode }) => ($isMobileMode ? '-0.26px' : '-0.28px')};
    ${({ $isMobileMode }) =>
      $isMobileMode &&
      `
      max-width: 100%;
      white-space: pre-wrap;
      word-break: break-word;
      display: -webkit-box;
      -webkit-line-clamp: 6;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
  }
`;

/* ─── 쟁점 섹션 스타일 ─── */

const IssueDivider = styled.hr`
  border: none;
  border-top: 0.3px solid rgba(0, 0, 0, 0.12);
  margin: 20px 0 16px;
  width: 100%;
`;

const IssuesTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 14px;

  @media (max-width: 599px) {
    flex-wrap: ${({ $isMobileMode }) => ($isMobileMode ? 'nowrap' : 'wrap')};
    gap: ${({ $isMobileMode }) => ($isMobileMode ? '8px' : '10px')};
    margin-bottom: 14px;
  }
`;

const IssuesTitle = styled.span`
  color: rgba(0, 0, 0, 0.8);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.28px;
  white-space: nowrap;

  @media (max-width: 599px) {
    font-size: ${({ $isMobileMode }) => ($isMobileMode ? '13px' : '14px')};
    letter-spacing: ${({ $isMobileMode }) => ($isMobileMode ? '-0.26px' : '-0.28px')};
  }
`;

const IssuesSubtitle = styled.span`
  color: rgba(0, 0, 0, 0.3);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.28px;
  white-space: nowrap;

  @media (max-width: 599px) {
    font-size: ${({ $isMobileMode }) => ($isMobileMode ? '13px' : '14px')};
    letter-spacing: ${({ $isMobileMode }) => ($isMobileMode ? '-0.26px' : '-0.28px')};
  }
`;

const IssuesChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 18px;

  @media (max-width: 599px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ $isMobileMode }) => ($isMobileMode ? '6px' : '8px')};
    margin-bottom: ${({ $isMobileMode }) => ($isMobileMode ? '0' : '18px')};
    width: 100%;
    flex-grow: 0;
    flex-shrink: 0;
  }
`;

const EmptyIssuesText = styled.span`
  color: rgba(0, 0, 0, 0.42);
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.26px;
`;

const baseChip = `
  display: flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  box-sizing: border-box;
  padding: 0 16px 0 8px;
  border-radius: 56px;
  border: 0.3px solid rgba(0, 0, 0, 0.2);
  background: #292929;

  @media (max-width: 599px) {
    height: 36px;
    padding: 0 14px 0 8px;
  }
`;

const AIChip = styled.div`
  ${baseChip}
`;

const UserChip = styled.div`
  ${baseChip}
`;

const AIBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 6px;
  border-radius: 100px;
  border: 0.5px solid #1ae276;
  background: linear-gradient(90deg, rgba(26, 226, 118, 0.3) 33.51%, rgba(58, 125, 255, 0.3) 86.88%);
  color: #fff;
  font-family: Pretendard;
  font-size: 8px;
  font-weight: 500;
  line-height: normal;
  white-space: nowrap;
`;

const UserBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 6px;
  border-radius: 100px;
  border: 0.5px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-family: Pretendard;
  font-size: 8px;
  font-weight: 500;
  line-height: normal;
  white-space: nowrap;
`;

const ChipText = styled.span`
  color: #fff;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  white-space: nowrap;
  padding: 0 4px;

  @media (max-width: 599px) {
    font-size: ${({ $isMobileMode }) => ($isMobileMode ? '13px' : '14px')};
    padding: ${({ $isMobileMode }) => ($isMobileMode ? '0 2px' : '0 4px')};
  }
`;

const ChipCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  opacity: 0.7;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
  }
`;

const AddIssueButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  padding: 10px 16px 10px 20px;
  border-radius: 56px;
  border: 0.3px solid rgba(0, 0, 0, 0.2);
  background: #fff;
  color: rgba(0, 0, 0, 0.6);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease;

  &:hover {
    background: #f5f5f5;
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.4;
  }

  @media (max-width: 599px) {
    height: ${({ $isMobileMode }) => ($isMobileMode ? '32px' : '36px')};
    padding: ${({ $isMobileMode }) => ($isMobileMode ? '0 16px' : '10px 16px 10px 20px')};
    gap: ${({ $isMobileMode }) => ($isMobileMode ? '6px' : '8px')};
    font-size: ${({ $isMobileMode }) => ($isMobileMode ? '12px' : '14px')};
    align-self: ${({ $isMobileMode }) => ($isMobileMode ? 'flex-start' : 'auto')};
    margin-top: ${({ $isMobileMode }) => ($isMobileMode ? '2px' : '0')};
  }
`;

const InputChipWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  box-sizing: border-box;
  padding: 0 8px 0 16px;
  border-radius: 56px;
  border: 0.3px solid rgba(0, 0, 0, 0.2);
  background: #fff;
  min-width: 160px;

  @media (max-width: 599px) {
    min-width: ${({ $isMobileMode }) => ($isMobileMode ? '106px' : '160px')};
    height: ${({ $isMobileMode }) => ($isMobileMode ? '32px' : '36px')};
    align-self: ${({ $isMobileMode }) => ($isMobileMode ? 'flex-start' : 'auto')};
    margin-top: ${({ $isMobileMode }) => ($isMobileMode ? '2px' : '0')};
  }
`;

const IssueInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: rgba(0, 0, 0, 0.6);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.28px;
  min-width: 80px;

  &::placeholder {
    color: rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 599px) {
    font-size: ${({ $isMobileMode }) => ($isMobileMode ? '12px' : '14px')};
    min-width: ${({ $isMobileMode }) => ($isMobileMode ? '38px' : '80px')};
  }
`;

const ConfirmBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
`;

const CancelBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
`; 
